import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiTrendingUp, FiTrendingDown, FiX, FiTarget, FiShield, FiCheck, FiChevronDown } from 'react-icons/fi';
import { URL_API } from '../config/endpoints';

const iconPath = (sym) => `/access/coinIcon/${String(sym || '').toLowerCase()}.png`;

const fmtUsd = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPrice = (p) => {
  const n = Number(p || 0);
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n ? n.toPrecision(4) : '—';
};
const fmtCoins = (c) => {
  const n = Number(c || 0);
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return n.toPrecision(4);
};
const fmtSigned = (v) => `${Number(v) >= 0 ? '+' : '−'}$${fmtUsd(Math.abs(Number(v || 0)))}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const s = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z';
    return new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const GREEN = '#2EBD85';
const RED = '#F6465D';

const closeMeta = (reason) => {
  if (reason === 'tp') return { label: 'Take Profit', short: 'TP', color: GREEN };
  if (reason === 'sl') return { label: 'Stop Loss', short: 'SL', color: RED };
  return { label: 'Закрыто вручную', short: 'Manual', color: '#9AA4B2' };
};

/**
 * Торговая панель под графиком (long/short БЕЗ плеча, маржа в USDT).
 * TP/SL — ценовые уровни-триггеры (как на Binance): вводишь цену → видишь
 * расчётный профит/убыток; сторона (Long/Short) активна по корректной стороне
 * уровня. Реально срабатывают на бэкенде (монитор _tpsl_monitor). Детали
 * открытой/закрытой сделки — в нижней модалке (через портал в body).
 */
export default function TradePanel({ userId, symbol, price, onAfterTrade }) {
  const [amount, setAmount] = useState('');
  const [openPos, setOpenPos] = useState([]);
  const [closedPos, setClosedPos] = useState([]);
  const [tradeUsdt, setTradeUsdt] = useState(0);
  const [tab, setTab] = useState('open');
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  const [closingId, setClosingId] = useState(null);

  // Пошаговый флоу: сумма → сторона → TP/SL → открыть
  const [side, setSide] = useState(null); // null | 'long' | 'short'
  const [tpOn, setTpOn] = useState(false);
  const [slOn, setSlOn] = useState(false);
  const [tpPrice, setTpPrice] = useState('');
  const [slPrice, setSlPrice] = useState('');

  // выбранная закрытая сделка → нижняя модалка деталей { id }
  const [detail, setDetail] = useState(null);
  // раскрытые карточки открытых сделок (аккордеон, можно несколько)
  const [expandedOpen, setExpandedOpen] = useState(() => new Set());
  const toggleExpand = (id) => setExpandedOpen((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // подтверждение закрытия сделки (id позиции)
  const [confirmClose, setConfirmClose] = useState(null);
  // конфиг фьючерсов (минималка + комиссия) — из публичного конфига, правится в /config бота
  const [cfg, setCfg] = useState({ futures_min_usd: 3, futures_fee_mode: 'percent', futures_fee_value: 1 });
  useEffect(() => {
    fetch(`${URL_API}/public_config`)
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.futures_min_usd === 'number') {
          setCfg({
            futures_min_usd: Number(d.futures_min_usd) || 0,
            futures_fee_mode: d.futures_fee_mode || 'percent',
            futures_fee_value: Number(d.futures_fee_value) || 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // блокируем прокрутку фона, пока открыта любая модалка
  useEffect(() => {
    if (!detail && !confirmClose) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [detail, confirmClose]);

  const fetchPositions = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${URL_API}/positions?user_id=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (!mounted.current) return;
      setOpenPos(Array.isArray(data?.open) ? data.open : []);
      setClosedPos(Array.isArray(data?.closed) ? data.closed : []);
      setTradeUsdt(Number(data?.trade_usdt || 0));
    } catch { /* сеть моргнула */ }
  }, [userId]);

  useEffect(() => {
    fetchPositions();
    const t = setInterval(fetchPositions, 1500);
    return () => clearInterval(t);
  }, [fetchPositions]);

  // live-PnL для позиций по ТЕКУЩЕМУ символу — по цене с графика (~1с)
  const livePnl = useCallback((p) => {
    if (p && p.symbol === symbol && price && Number(price) > 0) {
      const cur = Number(price);
      const pnl = p.side === 'short' ? p.coins * (p.entry - cur) : p.coins * (cur - p.entry);
      return { price: cur, pnl, pnl_pct: p.margin ? (pnl / p.margin) * 100 : 0, value: p.margin + pnl };
    }
    return { price: p.price, pnl: p.pnl, pnl_pct: p.pnl_pct, value: p.value };
  }, [symbol, price]);

  const amt = parseFloat(amount);
  const validAmt = Number.isFinite(amt) && amt > 0;
  const market = Number(price) || 0;

  // Комиссия открытия + минималка (из публичного конфига, правится в /config бота)
  const feeUsd = validAmt ? (cfg.futures_fee_mode === 'fixed' ? cfg.futures_fee_value : (amt * cfg.futures_fee_value) / 100) : 0;
  const minOk = validAmt && amt >= cfg.futures_min_usd - 1e-9;
  const enough = validAmt && (amt + feeUsd) <= tradeUsdt + 1e-9; // покрыть маржу + комиссию

  const tpVal = parseFloat(tpPrice);
  const slVal = parseFloat(slPrice);
  const tpSet = tpOn && Number.isFinite(tpVal) && tpVal > 0;
  const slSet = slOn && Number.isFinite(slVal) && slVal > 0;
  const tpReady = !tpOn || tpSet;
  const slReady = !slOn || slSet;

  // сторона выбрана заранее → уровни проверяем под неё:
  //   long → TP выше / SL ниже текущей; short → наоборот
  const tpOkForSide = !tpSet || (side === 'long' ? tpVal > market : tpVal < market);
  const slOkForSide = !slSet || (side === 'long' ? slVal < market : slVal > market);
  const sideValid = !!side && tpOkForSide && slOkForSide;

  const pnlAt = (lvl) => {
    if (!validAmt || !market || !(lvl > 0) || !side) return null;
    return side === 'long' ? (amt * (lvl - market)) / market : (amt * (market - lvl)) / market;
  };
  const projTp = tpSet ? pnlAt(tpVal) : null;
  const projSl = slSet ? pnlAt(slVal) : null;

  const canOpen = validAmt && enough && minOk && market > 0 && tpReady && slReady && sideValid && !opening;

  const fillMax = () => {
    if (tradeUsdt > 0) { setError(''); setAmount(String(Math.floor(tradeUsdt * 100) / 100)); }
  };

  // выбор стороны: при смене сбрасываем введённые уровни (они под другую сторону)
  const pickSide = (s) => {
    setError('');
    if (s !== side) { setTpPrice(''); setSlPrice(''); }
    setSide(s);
  };

  const openTrade = useCallback(async () => {
    setError('');
    if (!canOpen) {
      if (validAmt && !minOk) setError(`Минимальная сумма сделки — ${cfg.futures_min_usd}$`);
      else if (!side) setError('Выберите Long или Short');
      else if (validAmt && !enough) setError('Недостаточно средств (маржа + комиссия)');
      else if (tpOn && !tpSet) setError('Укажите цену Take Profit');
      else if (slOn && !slSet) setError('Укажите цену Stop Loss');
      else if (side === 'long') setError('Для Long: Take Profit выше, Stop Loss ниже текущей цены');
      else setError('Для Short: Take Profit ниже, Stop Loss выше текущей цены');
      return;
    }
    setOpening(true);
    try {
      const body = { user_id: parseInt(userId, 10) || 0, symbol, side, amount: amt };
      if (tpSet) body.tp_price = tpVal;
      if (slSet) body.sl_price = slVal;
      const res = await fetch(`${URL_API}/position/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data?.detail || data;
        setError(d?.message || 'Не удалось открыть сделку');
      } else {
        setAmount(''); setSide(null); setTpOn(false); setSlOn(false); setTpPrice(''); setSlPrice('');
        setTab('open');
        await fetchPositions();
        onAfterTrade && onAfterTrade();
      }
    } catch {
      setError('Ошибка сети');
    } finally {
      if (mounted.current) setOpening(false);
    }
  }, [canOpen, side, validAmt, enough, minOk, cfg, tpOn, slOn, tpSet, slSet, userId, symbol, amt, tpVal, slVal, fetchPositions, onAfterTrade]);

  const closeTrade = useCallback(async (id) => {
    setError('');
    setClosingId(id);
    try {
      const res = await fetch(`${URL_API}/position/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(userId, 10) || 0, position_id: id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data?.detail || data;
        setError(d?.message || 'Не удалось закрыть сделку');
      } else {
        await fetchPositions();
        onAfterTrade && onAfterTrade();
      }
    } catch {
      setError('Ошибка сети');
    } finally {
      if (mounted.current) setClosingId(null);
    }
  }, [userId, fetchPositions, onAfterTrade]);

  // ── модалка деталей закрытой сделки (история): свежие данные по id ──
  const detailPos = detail ? (closedPos.find((p) => p.id === detail.id) || null) : null;

  return (
    <div className="tp">
      <style>{`
        .tp *{box-sizing:border-box}
        .tp-card{background:rgba(31,31,37,0.55);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);border:1px solid rgba(46,46,58,0.7);border-radius:16px;padding:16px;margin-top:14px}
        .tp-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:13px}
        .tp-title{color:#EAECEF;font-size:14px;font-weight:700;letter-spacing:.2px}
        .tp-pair{font-size:12px;color:#848E9C;font-variant-numeric:tabular-nums}
        .tp-pair b{color:#EAECEF;font-weight:700}
        .tp-pair i{color:#5E6673;font-style:normal}
        .tp-pair .px{color:#B7BDC8;margin-left:7px;font-weight:600}

        .tp-amount{background:rgba(20,20,25,0.55);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);border:1px solid rgba(46,46,58,0.7);border-radius:12px;padding:9px 13px;transition:border-color .2s ease}
        .tp-amount:focus-within{border-color:#60BDFF}
        .tp-amt-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:3px}
        .tp-amt-lbl{color:#848E9C;font-size:10.5px;font-weight:600;letter-spacing:.3px}
        .tp-avail{background:none;border:none;cursor:pointer;color:#60BDFF;font-size:11.5px;font-weight:600;padding:0;font-variant-numeric:tabular-nums;transition:opacity .15s ease}
        .tp-avail:hover{opacity:.75}
        .tp-amt-row{display:flex;align-items:baseline;gap:7px}
        .tp-cur{color:#848E9C;font-size:17px;font-weight:600;line-height:1}
        .tp-input{flex:1;min-width:0;background:none;border:none;outline:none;color:#EAECEF;font-size:21px;font-weight:700;letter-spacing:-.4px;font-variant-numeric:tabular-nums;line-height:1.05}
        .tp-input::placeholder{color:#454C58}
        .tp-est{color:#848E9C;font-size:11.5px;white-space:nowrap;font-variant-numeric:tabular-nums}

        /* ── TP/SL ── */
        .tp-risk{margin-top:11px;display:flex;flex-direction:column;gap:9px}
        .tp-ck{display:flex;align-items:center;gap:10px;cursor:pointer;user-select:none}
        .tp-box{width:19px;height:19px;border-radius:6px;border:1.6px solid #3A4150;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .16s ease;background:rgba(255,255,255,0.02)}
        .tp-ck.tp-on .tp-box{background:#60BDFF;border-color:#60BDFF}
        .tp-ck.sl-on .tp-box{background:${RED};border-color:${RED}}
        .tp-ck-ic{display:flex;flex-shrink:0}
        .tp-ck-lbl{color:#C7CDD8;font-size:13px;font-weight:600;flex:1}
        .tp-ck-side{font-size:12px;font-weight:700;font-variant-numeric:tabular-nums}
        .tp-risk-body{overflow:hidden;animation:tpExpand .24s ease both;padding-left:29px;display:flex;flex-direction:column;gap:6px}
        .tp-amtfield{display:flex;align-items:center;gap:8px;background:rgba(20,20,25,0.55);border:1px solid rgba(46,46,58,0.7);border-radius:9px;padding:8px 12px;transition:border-color .15s ease}
        .tp-amtfield.tp:focus-within{border-color:${GREEN}}
        .tp-amtfield.sl:focus-within{border-color:${RED}}
        .tp-amtfield .cur{color:#848E9C;font-size:15px;font-weight:700}
        .tp-amtfield input{flex:1;min-width:0;background:none;border:none;outline:none;color:#EAECEF;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums}
        .tp-amtfield input::placeholder{color:#454C58;font-weight:600;font-size:14px}
        .tp-fhint{font-size:11px;color:#7E8794}
        .tp-warn{color:${RED};font-size:11px}

        /* пошаговые секции */
        .tp-step{margin-top:14px;padding-top:14px;border-top:1px solid rgba(46,46,58,0.5);animation:tpExpand .24s ease both}
        .tp-step-lbl{color:#848E9C;font-size:10.5px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between}
        .tp-step-mkt{color:#6B7480;font-weight:600;text-transform:none;letter-spacing:0;font-variant-numeric:tabular-nums}
        .tp-side{display:flex;gap:9px}
        .tp-side-btn{flex:1;border:1px solid rgba(70,70,88,0.7);background:rgba(255,255,255,0.02);border-radius:11px;padding:11px 0;cursor:pointer;font-size:14px;font-weight:700;color:#9AA4B2;display:flex;align-items:center;justify-content:center;gap:7px;transition:all .16s ease}
        .tp-side-btn:not(.on):hover{border-color:#5A6273}
        .tp-side-btn.long.on{background:rgba(46,189,133,.16);border-color:${GREEN};color:${GREEN}}
        .tp-side-btn.short.on{background:rgba(246,70,93,.16);border-color:${RED};color:${RED}}

        .tp-open{width:100%;border:none;border-radius:12px;padding:14px 0;margin-top:16px;cursor:pointer;font-size:15px;font-weight:700;letter-spacing:.3px;color:#fff;display:flex;align-items:center;justify-content:center;gap:8px;transition:background-color .18s ease,transform .1s ease,box-shadow .18s ease}
        .tp-open.long{background:${GREEN};box-shadow:0 7px 20px rgba(46,189,133,.28)}
        .tp-open.long:hover{background:#28A877}
        .tp-open.short{background:${RED};box-shadow:0 7px 20px rgba(246,70,93,.28)}
        .tp-open.short:hover{background:#E23E53}
        .tp-open:active{transform:scale(.99)}
        .tp-open:disabled{background:#2A2F3A;color:#5E6673;cursor:not-allowed;transform:none;box-shadow:none}

        .tp-tabs{position:relative;display:flex;border-bottom:1px solid #2A2F3A;margin-bottom:14px}
        .tp-tabs button{flex:1;border:none;background:none;cursor:pointer;padding:11px 0;font-size:13px;font-weight:600;color:#848E9C;transition:color .2s ease;display:flex;align-items:center;justify-content:center;gap:6px}
        .tp-tabs button.on{color:#EAECEF}
        .tp-tabs .cnt{font-size:11px;font-weight:700;color:#848E9C;background:#2A2F3A;border-radius:20px;padding:1px 7px;transition:all .2s ease}
        .tp-tabs button.on .cnt{color:#0B0E11;background:#60BDFF}
        .tp-ink{position:absolute;bottom:-1px;height:2px;width:50%;background:#60BDFF;border-radius:2px;transition:transform .28s cubic-bezier(.4,0,.2,1)}

        .tp-empty{color:#6B7480;font-size:13px;text-align:center;padding:26px 0}
        .tp-hint{color:#848E9C;font-size:12px;margin-top:10px;line-height:1.45}
        .tp-err{color:${RED};font-size:12px;margin-top:10px}

        /* минималистичная карта открытой сделки */
        .tp-pos{background:rgba(31,31,37,0.5);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);border:1px solid rgba(46,46,58,0.7);border-radius:13px;padding:11px 12px;animation:tpIn .22s ease both}
        .tp-pos+.tp-pos{margin-top:9px}
        .tp-pos-main{display:flex;align-items:center;justify-content:space-between;cursor:pointer}
        .tp-sym{display:flex;align-items:center;gap:8px}
        .tp-sym img{width:23px;height:23px;border-radius:50%}
        .tp-sym b{color:#EAECEF;font-weight:700;font-size:14px}
        .tp-badge{font-size:10px;font-weight:700;letter-spacing:.5px;padding:3px 8px;border-radius:6px}
        .tp-badge.long{color:${GREEN};background:rgba(46,189,133,.14)}
        .tp-badge.short{color:${RED};background:rgba(246,70,93,.14)}
        .tp-pos.on{border-color:rgba(96,189,255,0.22)}
        .tp-pos-right{display:flex;align-items:center;gap:6px}
        .tp-pnl{font-weight:700;font-size:15px;font-variant-numeric:tabular-nums;line-height:1.15}
        .tp-pnl-pct{font-size:11px;font-weight:600;font-variant-numeric:tabular-nums}
        .tp-chev{color:#5E6673;flex-shrink:0;transition:transform .2s ease}
        .tp-pos-exp{margin-top:10px;padding-top:6px;border-top:1px solid #23232B;animation:tpExpand .2s ease both}
        .tp-rows2{display:flex;flex-direction:column;margin-bottom:11px}
        .tp-row2{display:flex;align-items:center;justify-content:space-between;padding:7px 0;border-bottom:1px solid #1E1E26;font-size:12.5px}
        .tp-row2:last-child{border-bottom:none}
        .tp-row2 .k{color:#7E8794}
        .tp-row2 .v{color:#E7E9EE;font-weight:600;font-variant-numeric:tabular-nums}
        .tp-row2 .sub{color:#6B7480;font-weight:500}
        .tp-close{width:100%;border:none;background:#FFFFFF;color:#0A0E16;border-radius:10px;padding:11px 0;cursor:pointer;font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;gap:6px;transition:filter .15s ease}
        .tp-close:hover{filter:brightness(0.95)}
        .tp-close:disabled{cursor:wait;opacity:.6}

        .tp-hist{display:flex;align-items:center;justify-content:space-between;background:rgba(31,31,37,0.5);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);border:1px solid rgba(46,46,58,0.7);border-radius:11px;padding:10px 12px;cursor:pointer;animation:tpIn .22s ease both;transition:border-color .15s ease,background-color .15s ease}
        .tp-hist:hover{border-color:#3A4150;background:rgba(40,40,50,0.55)}
        .tp-hist+.tp-hist{margin-top:8px}
        .tp-hist img{width:21px;height:21px;border-radius:50%}
        .tp-rtag{font-size:9.5px;font-weight:700;letter-spacing:.3px;padding:1.5px 6px;border-radius:5px;margin-left:6px}

        /* нижняя модалка (портал в body) */
        .tp-sheet-ov{position:fixed;inset:0;z-index:4000;background:rgba(0,0,0,0.62);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);display:flex;align-items:flex-end;justify-content:center;animation:tpFade .2s ease}
        .tp-sheet{width:100%;max-width:480px;max-height:88vh;background:#16161B;border-top-left-radius:24px;border-top-right-radius:24px;border:1px solid #2A2A33;box-shadow:0 -10px 40px rgba(0,0,0,0.5);display:flex;flex-direction:column;overflow:hidden;animation:tpUp .3s cubic-bezier(.22,1,.36,1)}
        .tp-grip{width:42px;height:5px;border-radius:3px;background:#3A3A46;margin:10px auto 0}
        .tp-sh-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px 8px}
        .tp-sh-x{width:34px;height:34px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid #2E2E3A;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .tp-sh-pnl{padding:4px 18px 12px}
        .tp-sh-pnlbox{border-radius:16px;padding:15px;display:flex;flex-direction:column;align-items:center;gap:2px}
        .tp-sh-rows{padding:2px 18px 10px;overflow-y:auto}
        .tp-row{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid #202028;font-size:13px}
        .tp-row:last-child{border-bottom:none}
        .tp-row .k{color:#7E8794}
        .tp-row .v{color:#E7E9EE;font-weight:600;font-variant-numeric:tabular-nums}
        .tp-sh-foot{padding:6px 18px 20px}

        /* подтверждение закрытия */
        .tp-confirm-ov{position:fixed;inset:0;z-index:4100;background:rgba(0,0,0,0.62);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px;animation:tpFade .18s ease}
        .tp-confirm{width:100%;max-width:300px;background:#16161B;border:1px solid #2A2A33;border-radius:18px;box-shadow:0 20px 50px rgba(0,0,0,0.5);padding:20px 20px 16px;animation:tpPop .22s cubic-bezier(.22,1,.36,1)}
        .tp-cf-title{color:#fff;font-size:16px;font-weight:700;text-align:center}
        .tp-cf-pl{font-size:14px;font-weight:700;text-align:center;margin-top:7px;font-variant-numeric:tabular-nums}
        .tp-cf-actions{display:flex;gap:10px;margin-top:18px}
        .tp-cf-no{flex:1;border:1px solid #2E2E3A;background:rgba(255,255,255,0.03);color:#C7CDD8;border-radius:11px;padding:11px 0;cursor:pointer;font-size:14px;font-weight:700;transition:background-color .15s ease}
        .tp-cf-no:hover{background:rgba(255,255,255,0.06)}
        .tp-cf-yes{flex:1;border:none;background:#FFFFFF;color:#0A0E16;border-radius:11px;padding:11px 0;cursor:pointer;font-size:14px;font-weight:700;transition:filter .15s ease}
        .tp-cf-yes:hover{filter:brightness(.95)}
        .tp-cf-yes:disabled,.tp-cf-no:disabled{opacity:.6;cursor:wait}

        @keyframes tpIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
        @keyframes tpExpand{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
        @keyframes tpFade{from{opacity:0}to{opacity:1}}
        @keyframes tpUp{from{transform:translateY(100%)}to{transform:none}}
        @keyframes tpPop{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:none}}
      `}</style>

      {/* ── Открыть сделку ── */}
      <div className="tp-card">
        <div className="tp-head">
          <span className="tp-title">Открыть сделку</span>
          <span className="tp-pair"><b>{symbol}</b><i>/USDT</i><span className="px">${fmtPrice(price)}</span></span>
        </div>

        {/* Сумма (USDT) */}
        <div className="tp-amount">
          <div className="tp-amt-top">
            <span className="tp-amt-lbl">Сумма, USDT</span>
            <span className="tp-avail" role="button" onClick={fillMax}>Доступно ${fmtUsd(tradeUsdt)}</span>
          </div>
          <div className="tp-amt-row">
            <span className="tp-cur">$</span>
            <input
              className="tp-input"
              value={amount}
              onChange={(e) => { setError(''); setAmount(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')); }}
              inputMode="decimal"
              placeholder="0.00"
            />
            {validAmt && market > 0 && (
              <span className="tp-est">≈ {fmtCoins(amt / market)} {symbol}</span>
            )}
          </div>
        </div>

        {/* Минималка + комиссия открытия */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11.5, color: '#7E8794' }}>
          <span style={{ color: validAmt && !minOk ? RED : '#7E8794' }}>Мин. {cfg.futures_min_usd}$</span>
          <span>Комиссия открытия: <span style={{ color: '#C7CDD8', fontWeight: 600 }}>{cfg.futures_fee_mode === 'fixed' ? `${cfg.futures_fee_value}$` : `${cfg.futures_fee_value}%`}{validAmt ? ` ≈ $${fmtUsd(feeUsd)}` : ''}</span></span>
        </div>

        {/* Шаг 2: направление (появляется после ввода суммы) */}
        {validAmt && (
          <div className="tp-step">
            <div className="tp-step-lbl">Направление</div>
            <div className="tp-side">
              <button className={`tp-side-btn long${side === 'long' ? ' on' : ''}`} onClick={() => pickSide('long')}>
                <FiTrendingUp size={16} /> Long
              </button>
              <button className={`tp-side-btn short${side === 'short' ? ' on' : ''}`} onClick={() => pickSide('short')}>
                <FiTrendingDown size={16} /> Short
              </button>
            </div>
          </div>
        )}

        {/* Шаг 3: TP / SL — ввод ЦЕНЫ-триггера (после выбора стороны) */}
        {validAmt && side && (
          <div className="tp-step">
            <div className="tp-step-lbl">Take Profit / Stop Loss <span className="tp-step-mkt">тек. ${fmtPrice(price)}</span></div>
            <div className="tp-risk">
              {/* Take Profit */}
              <div className={`tp-ck${tpOn ? ' tp-on' : ''}`} onClick={() => { setError(''); setTpOn((v) => !v); }}>
                <span className="tp-box">{tpOn && <FiCheck size={13} color="#0B0E11" strokeWidth={3.5} />}</span>
                <span className="tp-ck-ic"><FiTarget size={15} color={tpOn ? GREEN : '#7E8794'} /></span>
                <span className="tp-ck-lbl">Take Profit</span>
                {tpSet && tpOkForSide && projTp != null && <span className="tp-ck-side" style={{ color: projTp >= 0 ? GREEN : RED }}>{fmtSigned(projTp)}</span>}
              </div>
              {tpOn && (
                <div className="tp-risk-body">
                  <div className="tp-amtfield tp" onClick={(e) => e.stopPropagation()}>
                    <span className="cur">$</span>
                    <input value={tpPrice} onChange={(e) => { setError(''); setTpPrice(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')); }} inputMode="decimal" placeholder="Цена закрытия (TP)" />
                  </div>
                  {tpSet && !tpOkForSide
                    ? <div className="tp-warn">Для {side === 'long' ? 'Long' : 'Short'} цена TP должна быть {side === 'long' ? 'выше' : 'ниже'} ${fmtPrice(price)}</div>
                    : tpSet && projTp != null
                      ? <div className="tp-fhint">Закроется при ${fmtPrice(tpVal)} → <span style={{ color: projTp >= 0 ? GREEN : RED, fontWeight: 700 }}>{fmtSigned(projTp)}</span></div>
                      : <div className="tp-fhint">Цена, на которой сделка закроется в плюс</div>}
                </div>
              )}

              {/* Stop Loss */}
              <div className={`tp-ck${slOn ? ' sl-on' : ''}`} onClick={() => { setError(''); setSlOn((v) => !v); }}>
                <span className="tp-box">{slOn && <FiCheck size={13} color="#fff" strokeWidth={3.5} />}</span>
                <span className="tp-ck-ic"><FiShield size={15} color={slOn ? RED : '#7E8794'} /></span>
                <span className="tp-ck-lbl">Stop Loss</span>
                {slSet && slOkForSide && projSl != null && <span className="tp-ck-side" style={{ color: projSl >= 0 ? GREEN : RED }}>{fmtSigned(projSl)}</span>}
              </div>
              {slOn && (
                <div className="tp-risk-body">
                  <div className="tp-amtfield sl" onClick={(e) => e.stopPropagation()}>
                    <span className="cur">$</span>
                    <input value={slPrice} onChange={(e) => { setError(''); setSlPrice(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')); }} inputMode="decimal" placeholder="Цена закрытия (SL)" />
                  </div>
                  {slSet && !slOkForSide
                    ? <div className="tp-warn">Для {side === 'long' ? 'Long' : 'Short'} цена SL должна быть {side === 'long' ? 'ниже' : 'выше'} ${fmtPrice(price)}</div>
                    : slSet && projSl != null
                      ? <div className="tp-fhint">Закроется при ${fmtPrice(slVal)} → <span style={{ color: projSl >= 0 ? GREEN : RED, fontWeight: 700 }}>{fmtSigned(projSl)}</span></div>
                      : <div className="tp-fhint">Цена, на которой сделка закроется принудительно</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {error && <div className="tp-err">{error}</div>}
        {tradeUsdt <= 0 && !validAmt && (
          <div className="tp-hint">Trade-баланс пуст. Пополни через Wallet → Transfer (Spot → Trade).</div>
        )}

        {/* Шаг 4: открыть сделку */}
        {validAmt && side && (
          <button className={`tp-open ${side}`} onClick={openTrade} disabled={!canOpen}>
            {opening ? 'Открываю…' : (side === 'long' ? <><FiTrendingUp size={16} /> Открыть Long</> : <><FiTrendingDown size={16} /> Открыть Short</>)}
          </button>
        )}
      </div>

      {/* ── Сделки: открытые / история ── */}
      <div className="tp-card">
        <div className="tp-tabs">
          <button className={tab === 'open' ? 'on' : ''} onClick={() => setTab('open')}>
            Открытые <span className="cnt">{openPos.length}</span>
          </button>
          <button className={tab === 'history' ? 'on' : ''} onClick={() => setTab('history')}>История</button>
          <span className="tp-ink" style={{ transform: tab === 'open' ? 'translateX(0)' : 'translateX(100%)' }} />
        </div>

        {tab === 'open' && (
          openPos.length === 0 ? (
            <div className="tp-empty">Нет открытых сделок</div>
          ) : (
            <div>
              {openPos.map((p) => {
                const live = livePnl(p);
                const green = live.pnl >= 0;
                const isLong = p.side === 'long';
                const col = green ? GREEN : RED;
                const exp = expandedOpen.has(p.id);
                return (
                  <div key={p.id} className={`tp-pos${exp ? ' on' : ''}`}>
                    <div className="tp-pos-main" onClick={() => toggleExpand(p.id)}>
                      <div className="tp-sym">
                        <img src={iconPath(p.symbol)} alt={p.symbol} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        <b>{p.symbol}</b>
                        <span className={`tp-badge ${isLong ? 'long' : 'short'}`}>{isLong ? 'LONG' : 'SHORT'}</span>
                      </div>
                      <div className="tp-pos-right">
                        <div style={{ textAlign: 'right' }}>
                          <div className="tp-pnl" style={{ color: col }}>{fmtSigned(live.pnl)}</div>
                          <div className="tp-pnl-pct" style={{ color: col }}>{green ? '▲' : '▼'} {Math.abs(live.pnl_pct).toFixed(2)}%</div>
                        </div>
                        <FiChevronDown className="tp-chev" size={18} style={{ transform: exp ? 'rotate(180deg)' : 'none' }} />
                      </div>
                    </div>
                    {exp && (
                      <div className="tp-pos-exp">
                        <div className="tp-rows2">
                          <div className="tp-row2"><span className="k">Сумма</span><span className="v">${fmtUsd(p.margin)} <span className="sub">· {fmtCoins(p.coins)} {p.symbol}</span></span></div>
                          <div className="tp-row2"><span className="k">Вход</span><span className="v">${fmtPrice(p.entry)}</span></div>
                          <div className="tp-row2"><span className="k">Сейчас</span><span className="v">${fmtPrice(live.price)}</span></div>
                          {p.take_profit ? <div className="tp-row2"><span className="k">Take Profit</span><span className="v" style={{ color: GREEN }}>${fmtPrice(p.take_profit)}{p.tp_pnl != null ? ` · ${fmtSigned(p.tp_pnl)}` : ''}</span></div> : null}
                          {p.stop_loss ? <div className="tp-row2"><span className="k">Stop Loss</span><span className="v" style={{ color: RED }}>${fmtPrice(p.stop_loss)}{p.sl_pnl != null ? ` · ${fmtSigned(p.sl_pnl)}` : ''}</span></div> : null}
                          <div className="tp-row2"><span className="k">Вернётся сейчас</span><span className="v">${fmtUsd(live.value)}</span></div>
                          <div className="tp-row2"><span className="k">Открыта</span><span className="v">{fmtDate(p.opened_at)}</span></div>
                        </div>
                        <button className="tp-close" onClick={(e) => { e.stopPropagation(); setConfirmClose(p.id); }} disabled={closingId === p.id}>
                          {closingId === p.id ? 'Закрываю…' : 'Закрыть'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === 'history' && (
          closedPos.length === 0 ? (
            <div className="tp-empty">История пуста</div>
          ) : (
            <div>
              {closedPos.map((p) => {
                const green = p.pnl >= 0;
                const isLong = p.side === 'long';
                const col = green ? GREEN : RED;
                const cm = closeMeta(p.close_reason);
                return (
                  <div key={p.id} className="tp-hist" onClick={() => setDetail({ id: p.id })}>
                    <div className="tp-sym">
                      <img src={iconPath(p.symbol)} alt={p.symbol} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontSize: 12.5, display: 'flex', alignItems: 'center' }}>
                          <span style={{ color: '#EAECEF', fontWeight: 700 }}>{p.symbol}</span>
                          <span style={{ color: isLong ? GREEN : RED, fontWeight: 700, fontSize: 10.5, marginLeft: 5 }}>{isLong ? 'LONG' : 'SHORT'}</span>
                          <span className="tp-rtag" style={{ color: cm.color, background: `${cm.color}1F` }}>{cm.short}</span>
                        </div>
                        <div style={{ color: '#6B7480', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>${fmtPrice(p.entry)} → ${fmtPrice(p.close)} · {fmtDate(p.closed_at)}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="tp-pnl" style={{ color: col, fontSize: 13.5 }}>{fmtSigned(p.pnl)}</div>
                      <div className="tp-pnl-pct" style={{ color: col }}>{green ? '▲' : '▼'} {Math.abs(p.pnl_pct).toFixed(2)}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* ── Нижняя модалка деталей (портал в body — поверх всего экрана) ── */}
      {detail && detailPos && createPortal((() => {
        const p = detailPos;
        const isLong = p.side === 'long';
        const isOpen = false; // модалка только для истории (закрытые)
        const live = isOpen ? livePnl(p) : null;
        const pnl = isOpen ? live.pnl : p.pnl;
        const pnlPct = isOpen ? live.pnl_pct : p.pnl_pct;
        const green = pnl >= 0;
        const col = green ? GREEN : RED;
        const cm = isOpen ? null : closeMeta(p.close_reason);
        return (
          <div className="tp-sheet-ov" onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
            <div className="tp-sheet">
              <div className="tp-grip" />
              <div className="tp-sh-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <img src={iconPath(p.symbol)} alt={p.symbol} style={{ width: 44, height: 44, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{p.symbol}/USDT</span>
                      <span className="tp-badge" style={{ color: isLong ? GREEN : RED, background: isLong ? 'rgba(46,189,133,.14)' : 'rgba(246,70,93,.14)' }}>{isLong ? 'LONG' : 'SHORT'}</span>
                    </div>
                    <div style={{ color: '#7E7E95', fontSize: 12.5, fontWeight: 500, marginTop: 2 }}>
                      {isOpen ? `Открыта · ${fmtDate(p.opened_at)}` : `Закрыто · ${fmtDate(p.closed_at)}`}
                    </div>
                  </div>
                </div>
                <button className="tp-sh-x" onClick={() => setDetail(null)} aria-label="Close"><FiX size={18} /></button>
              </div>

              <div className="tp-sh-pnl">
                <div className="tp-sh-pnlbox" style={{ background: green ? 'rgba(46,189,133,.10)' : 'rgba(246,70,93,.10)', border: `1px solid ${green ? 'rgba(46,189,133,.28)' : 'rgba(246,70,93,.28)'}` }}>
                  <div style={{ color: '#7E8794', fontSize: 12, fontWeight: 600 }}>{isOpen ? 'Текущий P/L' : 'Итог сделки'}</div>
                  <div style={{ color: col, fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtSigned(pnl)}</div>
                  <div style={{ color: col, fontSize: 13, fontWeight: 700 }}>{green ? '▲' : '▼'} {Math.abs(pnlPct).toFixed(2)}%</div>
                </div>
              </div>

              <div className="tp-sh-rows">
                {!isOpen && <div className="tp-row"><span className="k">Причина закрытия</span><span className="v" style={{ color: cm.color }}>{cm.label}</span></div>}
                <div className="tp-row"><span className="k">Сумма (маржа)</span><span className="v">${fmtUsd(p.margin)}</span></div>
                <div className="tp-row"><span className="k">Объём</span><span className="v">{fmtCoins(p.coins)} {p.symbol}</span></div>
                <div className="tp-row"><span className="k">Цена входа</span><span className="v">${fmtPrice(p.entry)}</span></div>
                <div className="tp-row"><span className="k">{isOpen ? 'Текущая цена' : 'Цена закрытия'}</span><span className="v">${fmtPrice(isOpen ? live.price : p.close)}</span></div>
                {p.take_profit ? <div className="tp-row"><span className="k">Take Profit</span><span className="v" style={{ color: GREEN }}>${fmtPrice(p.take_profit)}{p.tp_pnl != null ? ` · ${fmtSigned(p.tp_pnl)}` : ''}</span></div> : null}
                {p.stop_loss ? <div className="tp-row"><span className="k">Stop Loss</span><span className="v" style={{ color: RED }}>${fmtPrice(p.stop_loss)}{p.sl_pnl != null ? ` · ${fmtSigned(p.sl_pnl)}` : ''}</span></div> : null}
                <div className="tp-row"><span className="k">{isOpen ? 'Вернётся сейчас' : 'Возврат на баланс'}</span><span className="v">${fmtUsd(isOpen ? live.value : p.value)}</span></div>
                <div className="tp-row"><span className="k">Открыта</span><span className="v">{fmtDate(p.opened_at)}</span></div>
                {!isOpen && <div className="tp-row"><span className="k">Закрыта</span><span className="v">{fmtDate(p.closed_at)}</span></div>}
              </div>

              {isOpen && (
                <div className="tp-sh-foot">
                  <button className="tp-close" style={{ marginTop: 0 }} onClick={() => { closeTrade(p.id); setDetail(null); }} disabled={closingId === p.id}>
                    <FiX size={15} /> {closingId === p.id ? 'Закрываю…' : `Закрыть · вернётся $${fmtUsd(live.value)}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })(), document.body)}

      {/* ── Подтверждение закрытия сделки ── */}
      {confirmClose != null && createPortal((() => {
        const p = openPos.find((x) => x.id === confirmClose);
        if (!p) return null;
        const live = livePnl(p);
        const green = live.pnl >= 0;
        const col = green ? GREEN : RED;
        const busy = closingId === p.id;
        return (
          <div className="tp-confirm-ov" onClick={(e) => { if (e.target === e.currentTarget) setConfirmClose(null); }}>
            <div className="tp-confirm">
              <div className="tp-cf-title">Закрыть сделку?</div>
              <div className="tp-cf-pl" style={{ color: col }}>{green ? 'Прибыль' : 'Убыток'} {fmtSigned(live.pnl)}</div>
              <div className="tp-cf-actions">
                <button className="tp-cf-no" onClick={() => setConfirmClose(null)} disabled={busy}>Нет</button>
                <button className="tp-cf-yes" onClick={() => { const id = p.id; setConfirmClose(null); closeTrade(id); }} disabled={busy}>Да</button>
              </div>
            </div>
          </div>
        );
      })(), document.body)}
    </div>
  );
}
